import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { getAttendanceReport } from '../../services/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer } from 'recharts';

const AttendanceHistory = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState({ services: [], avg: 0 });
  const [from, setFrom] = useState(new Date(Date.now()-90*24*60*60*1000).toISOString().slice(0,10));
  const [to, setTo] = useState(new Date().toISOString().slice(0,10));

  useEffect(() => {
    getAttendanceReport({ from, to }).then(r => setData(r.data || { services: [], avg: 0 })).catch(() => {});
  }, [from, to]);

  const services = data.services || [];
  const avg = data.avg || 0;
  const max = services.length ? Math.max(...services.map(r => r.actual_count)) : 0;
  const chartData = [...services].reverse().map(s => ({ date: s.date, name: s.name, count: s.actual_count }));

  return (
    <div className="page-content">
      <div className="page-header">
        <div><h1>{t('nav.attendanceHistory')}</h1><p>{services.length}</p></div>
      </div>

      <div className="filters-row">
        <label style={{ fontSize: 13, fontWeight: 600 }}>{t('table.from')}:</label>
        <input className="filter-input" type="date" value={from} onChange={e => setFrom(e.target.value)} />
        <label style={{ fontSize: 13, fontWeight: 600 }}>{t('table.to')}:</label>
        <input className="filter-input" type="date" value={to} onChange={e => setTo(e.target.value)} />
      </div>

      <div className="stat-cards" style={{ gridTemplateColumns: 'repeat(3,1fr)', marginBottom: 20 }}>
        {[
          { label: t('nav.attendance'), value: avg, bg: '#ede9fe', color: '#7c3aed' },
          { label: t('common.total'), value: max, bg: '#dcfce7', color: '#16a34a' },
          { label: t('table.service'), value: services.length, bg: '#dbeafe', color: '#2563eb' },
        ].map((s, i) => (
          <div key={i} className="stat-card">
            <div className="stat-icon" style={{ background: s.bg, color: s.color, fontSize: 22 }}>📊</div>
            <div><div className="stat-label">{s.label}</div><div className="stat-value">{s.value}</div></div>
          </div>
        ))}
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header">{t('nav.attendance')}</div>
        <div className="card-body">
          {chartData.length === 0 ? (
            <div className="empty-state"><p>{t('finance.noRecords')}</p></div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={d => d?.slice(5)} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => [v, 'Attendance']} labelFormatter={l => chartData.find(d => d.date === l)?.name || l} />
                <ReferenceLine y={avg} stroke="#f59e0b" strokeDasharray="4 4" label={{ value: `Avg: ${avg}`, position: 'right', fontSize: 11 }} />
                <Bar dataKey="count" fill="#4f46e5" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="table-card">
        <table>
          <thead><tr><th>{t('common.date')}</th><th>{t('table.service')}</th><th>{t('table.type')}</th><th>{t('nav.attendance')}</th><th>% of Members</th></tr></thead>
          <tbody>
            {services.length === 0 ? (
              <tr><td colSpan={5}><div className="empty-state"><p>{t('finance.noRecords')}</p></div></td></tr>
            ) : services.map((r, i) => {
              const pct = r.total_members > 0 ? Math.round(r.actual_count / r.total_members * 100) : 0;
              return (
                <tr key={i} onClick={() => navigate(`/attendance/absentees?service=${r.id}`)} style={{ cursor: 'pointer' }} title={t('table.service')}>
                  <td data-label={t('common.date')}>{r.date}</td>
                  <td data-label={t('table.service')}>{r.name}</td>
                  <td data-label={t('table.type')} style={{ textTransform: 'capitalize' }}>{r.type}</td>
                  <td data-label={t('nav.attendance')}><strong>{r.actual_count}</strong></td>
                  <td data-label="% of Members">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ height: 8, width: `${pct}%`, maxWidth: 100, background: '#4f46e5', borderRadius: 4, minWidth: 4 }} />
                      {pct}%
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AttendanceHistory;
